import { batch, observable } from "@legendapp/state";
import type { QueryClient } from "@tanstack/react-query";
import type { KnowledgeBaseItem } from "@/services/knowledgeBaseService";

const MAX_IDS = 200;
const accessOrder: string[] = [];

/** Read-only projection of successful `knowledgebase-item` RQ entries (bounded LRU). */
export const kbDocumentsById$ = observable<Record<string, KnowledgeBaseItem | undefined>>({});

function patchItem(id: string, data: KnowledgeBaseItem) {
  const cur = kbDocumentsById$.peek();
  if (cur[id] === data) return;

  let next: Record<string, KnowledgeBaseItem | undefined> = { ...cur, [id]: data };
  const idx = accessOrder.indexOf(id);
  if (idx >= 0) accessOrder.splice(idx, 1);
  accessOrder.unshift(id);

  while (accessOrder.length > MAX_IDS) {
    const evict = accessOrder.pop();
    if (evict && evict !== id) {
      const { [evict]: _, ...rest } = next;
      next = rest;
    }
  }

  batch(() => {
    kbDocumentsById$.set(next);
  });
}

/**
 * One-way sync: React Query cache → Legend. Do not write server fields from Legend back into RQ.
 */
export function registerKnowledgeBaseQueryLegendSync(queryClient: QueryClient): () => void {
  const seedFromCache = () => {
    for (const q of queryClient.getQueryCache().getAll()) {
      const key = q.queryKey;
      if (key[0] !== "knowledgebase-item" || typeof key[1] !== "string") continue;
      if (q.state.status !== "success") continue;
      const id = key[1];
      const data = q.state.data as KnowledgeBaseItem | null | undefined;
      if (data) patchItem(id, data);
    }
  };

  seedFromCache();

  const unsub = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" && event.type !== "added") return;
    const q = event.query;
    if (!q) return;
    const key = q.queryKey;
    if (key[0] !== "knowledgebase-item" || typeof key[1] !== "string") return;
    if (q.state.status !== "success") return;
    const id = key[1];
    const data = q.state.data as KnowledgeBaseItem | null | undefined;
    if (!data) return;
    patchItem(id, data);
  });

  return () => {
    unsub();
  };
}
